import { useEffect, useRef } from "react";
import type { MutableRefObject } from "react";
import type { NavigateFunction } from "react-router-dom";

interface KeyboardShortcutsOptions {
  navigate: NavigateFunction;
  onOpenHelp: () => void;
  onOpenSearch: () => void;
}

const GO_TIMEOUT_MS = 1500;

const goRoutes = {
  t: "/today",
  c: "/cases",
  k: "/customers",
  m: "/messages",
  f: "/cases?view=follow-ups",
} as const;

export function useKeyboardShortcuts({
  navigate,
  onOpenHelp,
  onOpenSearch,
}: KeyboardShortcutsOptions): void {
  const goModeRef = useRef(false);
  const timerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (isEditableTarget(event.target)) return;
      if (handleCommandSearch(event, onOpenSearch)) return;
      if (handleHelp(event, onOpenHelp)) return;
      if (handleGoSequence(event, navigate, goModeRef, timerRef)) return;
      clearGoMode(goModeRef, timerRef);
    }

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      clearGoMode(goModeRef, timerRef);
    };
  }, [navigate, onOpenHelp, onOpenSearch]);
}

function handleCommandSearch(event: KeyboardEvent, onOpenSearch: () => void): boolean {
  if (event.key.toLowerCase() !== "k" || (!event.metaKey && !event.ctrlKey)) return false;
  event.preventDefault();
  onOpenSearch();
  return true;
}

function handleHelp(event: KeyboardEvent, onOpenHelp: () => void): boolean {
  if (event.key !== "?") return false;
  event.preventDefault();
  onOpenHelp();
  return true;
}

function handleGoSequence(
  event: KeyboardEvent,
  navigate: NavigateFunction,
  goModeRef: MutableRefObject<boolean>,
  timerRef: MutableRefObject<number | undefined>,
): boolean {
  const key = event.key.toLowerCase();
  if (!goModeRef.current && key === "g") {
    event.preventDefault();
    startGoMode(goModeRef, timerRef);
    return true;
  }
  if (!goModeRef.current) return false;
  event.preventDefault();
  clearGoMode(goModeRef, timerRef);
  const path = goRoutes[key as keyof typeof goRoutes];
  if (path !== undefined) void navigate(path);
  return true;
}

function startGoMode(
  goModeRef: MutableRefObject<boolean>,
  timerRef: MutableRefObject<number | undefined>,
): void {
  clearGoMode(goModeRef, timerRef);
  goModeRef.current = true;
  timerRef.current = window.setTimeout(() => {
    goModeRef.current = false;
    timerRef.current = undefined;
  }, GO_TIMEOUT_MS);
}

function clearGoMode(
  goModeRef: MutableRefObject<boolean>,
  timerRef: MutableRefObject<number | undefined>,
): void {
  goModeRef.current = false;
  if (timerRef.current !== undefined) {
    window.clearTimeout(timerRef.current);
    timerRef.current = undefined;
  }
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  return (
    tag === "input" ||
    tag === "textarea" ||
    tag === "select" ||
    target.isContentEditable
  );
}
